import { useFieldArray, useForm } from "react-hook-form";
("use client");

import { AnimatePresence, motion } from "motion/react";

import EMRSectionSearch from "@/app/components/ui/Reusable/EMRSectionSearch";
import {
  EMRdetailsProps,
  IEMRSectionSearchResponseData,
  IEMRUpdatePayloadProps,
} from "@/api_framework/api_modals/EMRs";
import Input from "@/app/components/ui/Inputs/Input";
import { debounce, toNumber } from "lodash";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStaticMedicineFrequencyAPI } from "@/app/hooks/api_hooks/emr/useStaticMedicineFrequencyAPI";
import { useStaticDurationAPI } from "@/app/hooks/api_hooks/emr/useStaticDurationAPI";
import { useStaticMedicineUnitsAPI } from "@/app/hooks/api_hooks/emr/useStaticMedicineUnitsAPI";
import { useStaticMedicineTimingAPI } from "@/app/hooks/api_hooks/emr/useStaticMedicineTimingAPI";
import { useStaticRoutesAPI } from "@/app/hooks/api_hooks/emr/useStaticRoutesAPI";
import InputWithSuggestionBox from "@/app/components/ui/Inputs/InputWithSuggestionBox";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";

import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import EMRItemsSuggestionsComponent from "../EMRItemsSuggestionsComponent";
import toast from "react-hot-toast";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import NestedTaperingDoseV2 from "./NestedTaperingDoseV2";
import { PlusIcon } from "lucide-react";
import { setTaperingIndex } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { LOCKED_CHILD_ITEM_IDS } from "@/constants";

type MedicationSectionFormFieldType = {
  medicine_id: string;
  name: string;
  dose?: string;
  dose_unit?: string;
  sku_form?: string | null;
  form?: string | null;
  form_name?: string | null;
  frequency?: string;
  x_value?: string;
  timing?: string;
  route?: string;
  duration?: string;
  duration_unit?: string;
  remark?: string;
  quantity: number;
  is_child?: boolean;
  tapering_doses: Array<{
    dose?: string;
    dose_unit?: string;
    frequency?: string;
    x_value?: string;
    duration?: string;
    duration_unit?: string;
    timing?: string;
    route?: string;
    remark?: string;
    quantity: number;
    is_child?: boolean;
  }>;
};

export interface IEMRMedicationSectionFormFields {
  medications: MedicationSectionFormFieldType[];
  instructions: string;
}

const MEDICINE_DROPDOWN_Z_INDEX = 20;

const isLockedChildMedicationItem = (item?: Partial<MedicationSectionFormFieldType>) =>
  Boolean(item?.is_child && item?.medicine_id && LOCKED_CHILD_ITEM_IDS.includes(item.medicine_id));

const getAutoDoseUnitFromItem = (item?: any) =>
  item?.sku_form?.trim?.() ||
  item?.form?.trim?.() ||
  item?.form_name?.trim?.() ||
  item?.skuForm?.trim?.() ||
  item?.skuFormName?.trim?.() ||
  null;

const normalizeDoseForPayload = (doseValue: unknown) => {
  if (doseValue === null || doseValue === undefined || doseValue === "") {
    return doseValue;
  }

  const parsedDose = Number(doseValue);
  if (!Number.isFinite(parsedDose)) {
    return doseValue;
  }

  return Number(parsedDose.toFixed(2));
};

const normalizeDurationForPayload = (durationValue: unknown) => {
  if (durationValue === null || durationValue === undefined || durationValue === "") {
    return durationValue;
  }

  const parsedDuration = Number(durationValue);
  if (!Number.isFinite(parsedDuration)) {
    return durationValue;
  }

  return Math.floor(parsedDuration);
};

const normalizeDoseUnitName = (value?: string | null) => value?.trim?.().toLowerCase() || "";
const hasFilledValue = (value: unknown) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return true;
  return String(value).trim() !== "";
};
const DURATION_NUMBER_FORMAT_VALIDATION = {
  pattern: {
    value: /^-?\d+(\.\d+)?$/,
    message: "Only numeric values",
  },
};
const DURATION_NUMERIC_ERROR_MESSAGE = { message: "Only numeric values", type: "pattern" as const };
const DURATION_NO_DECIMAL_ERROR_MESSAGE = { message: "No decimal values", type: "noDecimal" as const };
const DURATION_POSITIVE_ERROR_MESSAGE = { message: "Only positive values", type: "positiveValue" as const };
const DOSE_DECIMAL_ERROR_MESSAGE = { message: "Upto 2 decimals only", type: "pattern" as const };
const DOSE_POSITIVE_ERROR_MESSAGE = { message: "Only positive values", type: "validate" as const };
const DOSE_DECIMAL_VALIDATION = {
  pattern: {
    value: /^-?\d+(\.\d{1,2})?$/,
    message: "Upto 2 decimals only",
  },
};

const MedicinesSectionV2 = ({
  emrDetails,
  setEmrDetails,
  dropdownZIndex = MEDICINE_DROPDOWN_Z_INDEX,
}: EMRdetailsProps & { dropdownZIndex?: number }) => {
  const formHook = useForm<IEMRMedicationSectionFormFields>({
    mode: "onChange",
  });

  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const { execute: logAPI } = useClickLogAPI();
  const dispatch = useAppDispatch();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const customerId = emrDetails?.customer_details?.customer_id;
  const pet_id = emrDetails?.pet_details?.pet_id;
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  // RBAC: EMR_TEMPLATE_DELETE_LOCKED_ITEM — legacy role: super_admin
  const canDeleteLockedItem = useFeatureAccess("EMR_TEMPLATE_DELETE_LOCKED_ITEM");
  const medicineUnits = useAppSelector((state) => state.staticAPI?.medicine_units || []);

  const { data: medicineFrequencies, execute: fetchMedicineFrequencies } = useStaticMedicineFrequencyAPI();
  const { data: durations, execute: fetchDurations } = useStaticDurationAPI();
  const { execute: fetchUnits } = useStaticMedicineUnitsAPI();
  const { data: medicineTimings, execute: fetchMedicineTimings } = useStaticMedicineTimingAPI();
  const { data: routes, execute: fetchRoutes } = useStaticRoutesAPI();

  const [animateLast, setAnimateLast] = useState(false);

  const doseRef = useRef<HTMLInputElement>(null);
  const frequencyRef = useRef<HTMLInputElement>(null);
  const timingRef = useRef<HTMLInputElement>(null);
  const durationUnitRef = useRef<HTMLInputElement>(null);

  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const medicationFormHook = useFieldArray({
    name: "medications",
    control: formHook.control,
  });

  // Serialized copy of the medicines payload the server is known to hold.
  // `null` means the form is not in sync with any known server state — before
  // the first hydration, or while a direct add/remove call is in flight — and
  // auto-save stays quiet until hydration re-establishes it.
  const serverSnapshotRef = useRef<string | null>(null);

  const normalizeDurationUnitToDays = useCallback((medications: MedicationSectionFormFieldType[] = []) => {
    return (medications || []).map((medication) => ({
      ...medication,
      sku_form: medication?.sku_form || medication?.form || medication?.form_name || null,
      dose_unit:
        medication?.dose_unit || medication?.sku_form || medication?.form || medication?.form_name || undefined,
      duration_unit: "days",
      tapering_doses: (medication?.tapering_doses || []).map((taperingDose) => ({
        ...taperingDose,
        duration_unit: "days",
      })),
    }));
  }, []);

  const prepareMedicationsForPayload = useCallback((medications: MedicationSectionFormFieldType[] = []) => {
    return normalizeDurationUnitToDays(medications).map((medication) => {
      const parsedQuantity = toNumber(medication?.quantity);
      const safeQuantity =
        Number.isFinite(parsedQuantity) && parsedQuantity > 0
          ? Number.isInteger(parsedQuantity)
            ? parsedQuantity
            : Math.floor(parsedQuantity)
          : 1;

      return {
        ...medication,
        dose: normalizeDoseForPayload(medication?.dose),
        duration: normalizeDurationForPayload(medication?.duration),
        quantity: safeQuantity,
        tapering_doses: (medication?.tapering_doses || []).map((taperingDose) => {
          const parsedTaperingQuantity = toNumber(taperingDose?.quantity);
          const safeTaperingQuantity =
            Number.isFinite(parsedTaperingQuantity) && parsedTaperingQuantity > 0
              ? Number.isInteger(parsedTaperingQuantity)
                ? parsedTaperingQuantity
                : Math.floor(parsedTaperingQuantity)
              : 1;

          return {
            ...taperingDose,
            dose: normalizeDoseForPayload(taperingDose?.dose),
            duration: normalizeDurationForPayload(taperingDose?.duration),
            quantity: safeTaperingQuantity,
          };
        }),
      };
    });
  }, [normalizeDurationUnitToDays]);

  const buildMedicines = useCallback(
    (medications: MedicationSectionFormFieldType[], instructions: string) => ({
      medicines_added: prepareMedicationsForPayload(medications),
      medicines_instructions: instructions,
    }),
    [prepareMedicationsForPayload],
  );

  const currentMedicines = useCallback(() => {
    const { medications, instructions } = formHook.getValues();
    return buildMedicines(medications, instructions);
  }, [buildMedicines, formHook]);

  const hasUnsavedEdits = useCallback(
    () => serverSnapshotRef.current !== null && JSON.stringify(currentMedicines()) !== serverSnapshotRef.current,
    [currentMedicines],
  );

  // Persists the current form state, skipping the call when the server already
  // has it. Built from getValues() at call time, so a save scheduled before an
  // edit still sends the edit.
  const saveMedicines = useCallback(() => {
    const medicines = currentMedicines();
    const snapshot = JSON.stringify(medicines);
    if (snapshot === serverSnapshotRef.current) return;

    serverSnapshotRef.current = snapshot;
    updateEmrAPI(emr_id, {
      medicines,
      remove_children: emrDetails?.remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    }).then((res) => {
      // Save failed: forget the snapshot so the next edit re-sends everything.
      if (!res?.status) serverSnapshotRef.current = null;
    });
  }, [currentMedicines, emr_id, emr_status, emrDetails?.emr_status, emrDetails?.remove_children, updateEmrAPI]);

  const debouncedSave = useMemo(
    () =>
      debounce(() => {
        // No known server state yet — nothing to diff against, so nothing to save.
        if (serverSnapshotRef.current === null) return;
        saveMedicines();
      }, 1000),
    [saveMedicines],
  );

  useEffect(() => {
    // Fires for typed input and for the setValue/append/remove that the
    // suggestion boxes (frequency, timing, dose unit) and tapering rows use.
    // Nothing filters on RHF's `type` — hydration is neutralised by the
    // snapshot diff in saveMedicines instead, which needs no event metadata.
    const subscription = formHook.watch(() => debouncedSave());

    return () => {
      subscription.unsubscribe();
      debouncedSave.cancel();
    };
  }, [formHook, debouncedSave]);

  useEffect(() => {
    fetchMedicineFrequencies();
    fetchMedicineTimings();
    fetchDurations();
    fetchRoutes();
  }, []);

  useEffect(() => {
    if (!(medicineUnits?.length > 0)) {
      fetchUnits();
    }
  }, [medicineUnits?.length]);

  useEffect(() => {
    if (!emrDetails) return;

    debouncedSave.cancel();

    // Another section's save refreshed emrDetails with data that predates the
    // user's edits. Send them instead of replacing the form — hydrating here
    // would silently revert a just-picked frequency.
    if (hasUnsavedEdits()) {
      saveMedicines();
      return;
    }

    const medications = normalizeDurationUnitToDays(emrDetails?.medicines?.medicines_added || []).map((item) =>
      isLockedChildMedicationItem(item) && toNumber(item?.quantity) <= 0 ? { ...item, quantity: 1 } : item,
    );
    const instructions = emrDetails?.medicines?.medicines_instructions || "";

    medicationFormHook.replace(medications);
    formHook.setValue("instructions", instructions, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });

    // Baseline built from what we just wrote, not re-read from the form, so it
    // does not depend on replace() having flushed into getValues() yet.
    serverSnapshotRef.current = JSON.stringify(buildMedicines(medications, instructions));
  }, [emrDetails, normalizeDurationUnitToDays, debouncedSave, hasUnsavedEdits, saveMedicines, buildMedicines]);

  const unitItems = useSelectMenuReducer(medicineUnits, "dose_unit_name", "dose_unit_name");
  const medicineUnitMandatoryMap = useMemo(
    () =>
      (medicineUnits || []).reduce(
        (acc, unit) => {
          const normalizedDoseUnitName = normalizeDoseUnitName(unit?.dose_unit_name);
          if (!normalizedDoseUnitName) return acc;
          acc[normalizedDoseUnitName] = !!unit?.mandatory_field;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    [medicineUnits],
  );
  const isSkuFormMandatory = useCallback(
    (skuForm?: string | null) => {
      const normalizedSkuForm = normalizeDoseUnitName(skuForm);
      if (!normalizedSkuForm) return false;
      return !!medicineUnitMandatoryMap[normalizedSkuForm];
    },
    [medicineUnitMandatoryMap],
  );
  const frequencySuggestionGroups = useMemo(
    () =>
      (medicineFrequencies || [])
        .map((group) => ({
          title: group.frequency_category,
          options: (group.options || []).map((option) => option.frequency_name).filter(Boolean),
        }))
        .filter((group) => group.options.length > 0),
    [medicineFrequencies],
  );
  const frequencySuggestions = useMemo(
    () => frequencySuggestionGroups.flatMap((group) => group.options),
    [frequencySuggestionGroups],
  );
  const frequencyXValueRequirementMap = useMemo(
    () =>
      (medicineFrequencies || []).reduce(
        (acc, group) => {
          (group.options || []).forEach((option) => {
            if (option?.frequency_name) {
              acc[option.frequency_name] = !!option?.x_value;
            }
          });
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    [medicineFrequencies],
  );
  const timingSuggestionGroups = useMemo(
    () =>
      (medicineTimings || [])
        .map((group) => ({
          title: group.timing_category,
          options: (group.options || []).map((option) => option.timing_name).filter(Boolean),
        }))
        .filter((group) => group.options.length > 0),
    [medicineTimings],
  );
  const timingSuggestions = useMemo(
    () => timingSuggestionGroups.flatMap((group) => group.options),
    [timingSuggestionGroups],
  );
  const normalizedMedicineSuggestions = useMemo(
    () =>
      (emrDetails?.medicines?.medicines_suggestions || []).map((item: any) => ({
        ...item,
        name: item?.name || item?.variant_name || item?.item_name || "",
        vetic_variant_id: item?.vetic_variant_id || item?.medicine_id || item?.variant_id || null,
        sku_form: getAutoDoseUnitFromItem(item),
      })),
    [emrDetails?.medicines?.medicines_suggestions],
  );

  const isChilderRemovefromEMR = (index: number, item: any) => {
    // The payload below is built from getValues(), so any pending debounced
    // edit is already included. Clearing the snapshot lets the response
    // hydrate the form instead of being treated as a stale refresh.
    debouncedSave.cancel();
    serverSnapshotRef.current = null;
    medicationFormHook.remove(index);
    const existing = formHook.getValues("medications");
    let remove_children = null;

    if (item?.is_child) {
      remove_children = [...(emrDetails?.remove_children ?? []), item?.medicine_id];
    } else {
      remove_children = [...(emrDetails?.remove_children ?? [])];
    }

    const payload: IEMRUpdatePayloadProps = {
      medicines: {
        medicines_added: prepareMedicationsForPayload(existing as MedicationSectionFormFieldType[]),
        medicines_instructions: formHook.getValues("instructions"),
      },
      remove_children: remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };

    updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
    callLogAPILabDeleted(item?.medicine_id);
  };

  const handleAddMedication = async (item: IEMRSectionSearchResponseData, source?: string) => {
    const existing = formHook.getValues("medications");

    const isPresent = medicationFormHook.fields.filter((f) => f.name === item.name);
    if (isPresent.length > 0) {
      toast("Medications already added.");
      return;
    }

    // As above — after the duplicate early-return, so a rejected add leaves any
    // pending auto-save intact.
    debouncedSave.cancel();
    serverSnapshotRef.current = null;

    const autoDoseUnit = getAutoDoseUnitFromItem(item);

    const newItem = {
      name: item.name,
      tapering_doses: [],
      remark: "",
      medicine_id: item?.vetic_variant_id,
      dose_unit: autoDoseUnit || undefined,
      sku_form: autoDoseUnit,
      duration_unit: "days",
      quantity: 1,
      is_hd_stock: item?.is_hd_stock ?? undefined,
      prescription_type: item?.prescription_type,
    };

    setAnimateLast(true);

    const addedList = [...existing, newItem];

    const payload: IEMRUpdatePayloadProps = {
      medicines: {
        medicines_added: prepareMedicationsForPayload(addedList as MedicationSectionFormFieldType[]),
        medicines_instructions: formHook.getValues("instructions"),
      },
      remove_children: emrDetails?.remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };

    await updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });

    source && callLogSuggestionAPI(item);
  };

  useEffect(() => {
    if (!animateLast) return;
    const t = setTimeout(() => setAnimateLast(false), 600);
    return () => clearTimeout(t);
  }, [animateLast, emrDetails]);

  const callLogAPIBase = (
    item: string,
    eventType: "EMR_QUANTITY_PLUS" | "EMR_QUANTITY_MINUS" | "EMR_ITEM_DELETED" | "SUGGESTION_ITEMS",
  ) => {
    const fieldValue = formHook.watch("medications");

    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "EMR",
      client_id: customerId,
      emr_id: emr_id,
      pet_id: pet_id,
      click_on_search_item: item || undefined,
      medication_section: fieldValue || undefined,
      clinic_id: mappedClinicId,
      emr_search_type: "Medicines",
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };

    logAPI(logBody);
  };

  const callLogSuggestionAPI = (item: any) => callLogAPIBase(item, "SUGGESTION_ITEMS");

  const callLogAPILabDeleted = (item: any) => {
    callLogAPIBase(item, "EMR_ITEM_DELETED");
  };

  return (
    <div className=" w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      <div className="bg-[#F9FAFB] rounded-xl p-2  ">
        <div className="flex flex-col gap-2 pb-2 sm:flex-row sm:gap-10 sm:items-center">
          <h3 className="shrink-0 text-[#000000] text-sm font-medium tracking-tight42 sm:w-2/12">Medicines</h3>
          <div className="w-full sm:flex-1 sm:-mt-1">
            <EMRSectionSearch
              clinicId={mappedClinicId}
              emrType={emrDetails?.emr_type}
              onSelectItem={(item) => {
                handleAddMedication(item);
              }}
              onFlexibleAdd={(itemName) => {
                if (itemName !== "") {
                  medicationFormHook.append({
                    name: itemName,
                    tapering_doses: [],
                    remark: "",
                    medicine_id: null,
                    sku_form: null,
                    duration_unit: "days",
                    quantity: 1,
                  });
                }
              }}
              placeholder="Search & Add Medications"
              containerClassName="w-full sm:max-w-[600px]"
              itemActiveClassName="bg-[#f6f6f6] text-black"
              inputContainerClassName="w-full"
              flexibleAdd
              searchType="Medicines"
            />
          </div>
        </div>
        <div className="bg-white p-[10px] rounded-lg ">
          {medicationFormHook?.fields?.length > 0 && (
            <div className="rounded-xl overflow-x-auto">
              <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <colgroup>
                  <col className="w-[25%] lg:w-[24%]" />
                  <col className="w-[19%] lg:w-[18%]" />
                  <col className="w-[16%] lg:w-[15%]" />
                  <col className="w-[15%] lg:w-[14%]" />
                  <col className="w-[14%]" />
                  <col className="w-[7%] sm:w-[8%] lg:w-[11%]" />
                  <col className="w-[4%] sm:w-[3%] lg:w-[4%]" />
                </colgroup>
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-medium rounded-md">
                  <tr>
                    <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">
                      Item Names
                    </th>
                    <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Dose</th>
                    <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Frequency</th>
                    <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Timing</th>
                    <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Duration</th>
                    <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Remark</th>
                    <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Action</th>
                  </tr>
                </thead>
                <tbody className="overflow-hidden  rounded-xl w-full">
                  <AnimatePresence>
                    {medicationFormHook?.fields?.map((field, index) => {
                      const isFirst = index === 0;
                      const isLast = index === medicationFormHook?.fields?.length - 1;
                      const isSingle = medicationFormHook?.fields?.length === 1;
                      const selectedSkuForm = formHook.watch(`medications.${index}.sku_form`)?.trim?.() || null;
                      const hasAutoDoseUnit = !!selectedSkuForm;
                      const effectiveMandatoryValue = selectedSkuForm;
                      const isCurrentSkuFormMandatory = hasAutoDoseUnit && isSkuFormMandatory(effectiveMandatoryValue);
                      const selectedDose = formHook.watch(`medications.${index}.dose`);
                      const selectedFrequency = formHook.watch(`medications.${index}.frequency`);
                      const selectedTiming = formHook.watch(`medications.${index}.timing`);
                      const selectedDuration = formHook.watch(`medications.${index}.duration`);
                      const selectedXValue = formHook.watch(`medications.${index}.x_value`);
                      const showXValueInput = !!frequencyXValueRequirementMap[selectedFrequency];
                      const isDoseRequiredAndEmpty = isCurrentSkuFormMandatory && !hasFilledValue(selectedDose);
                      const isFrequencyRequiredAndEmpty = isCurrentSkuFormMandatory && !hasFilledValue(selectedFrequency);
                      const isTimingRequiredAndEmpty = isCurrentSkuFormMandatory && !hasFilledValue(selectedTiming);
                      const isDurationRequiredAndEmpty = isCurrentSkuFormMandatory && !hasFilledValue(selectedDuration);
                      const isXRequiredAndEmpty =
                        isCurrentSkuFormMandatory && showXValueInput && !hasFilledValue(selectedXValue);
                      const isLockedChildItem = isLockedChildMedicationItem(field);
                      const isDeleteDisabled = isLockedChildItem && !canDeleteLockedItem;
                      // stable key: prefer medicine_id (persisted id) or fallback to field.id
                      const rowKey = field.medicine_id ?? field.id ?? `${index}`;

                      // only the last row will use initial animation when animateLast is true
                      const initialProp = animateLast && isLast ? { opacity: 0, y: 10, scale: 0.97 } : undefined; // undefined = no initial mount animation

                      return (
                        <>
                          <motion.tr
                            key={rowKey}
                            initial={initialProp}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.97 }}
                            // transition={{ duration: 0.28 }}
                            transition={{ duration: 0.45, ease: "easeInOut" }}
                            className={`
    bg-white overflow-hidden
    ${isSingle ? "rounded-bl-xl" : ""}
    ${isFirst && !isSingle ? "rounded-t-xl" : ""}
    ${isLast && !isSingle ? "rounded-bl-xl" : ""}
  `}
                          >
                            <td
                              // className={`p-2 text-left `}
                              className={`p-2 text-left ${isSingle
                                ? "rounded-tl-xl rounded-bl-xl"
                                : `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-bl-xl" : ""}`.trim()
                                }`}
                            >
                              <div className="w-full relative">
                                <input hidden {...formHook.register(`medications.${index}.name`)} />
                                <input hidden {...formHook.register(`medications.${index}.sku_form`)} />
                                <div className="">
                                  <span className="text-[10px] sm:text-xs text-[#1F1F23] font-normal break-words">
                                    {formHook.getValues(`medications.${index}.name`)}
                                  </span>
                                  {field?.is_child && (
                                    <p className="items-center rounded-md  px-2 py-1 text-[10px] font-semibold w-[70px] ml-2  ring-1 ring-inset text-gray-500 bg-blue-50 ring-green-500/10 my-2">
                                      Suggested
                                    </p>
                                  )}
                                </div>
                                <div className="w-full flex justify-start items-center mt-1" >
                                  <button
                                    data-tooltip-id={"tapering_dose_button"}
                                    data-tooltip-content="Add tapering dose"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      dispatch(setTaperingIndex(index));
                                    }}
                                    className="h-7 px-2 rounded-md border border-dashed border-[#E5E7EB] bg-[#FAFAFA] text-[#8E8E93] text-[10px] font-semibold leading-none flex items-center gap-0.5 hover:bg-[#F5F5F5] transition-colors"
                                  >
                                    <span className="text-xs leading-none">+</span>
                                    <span className="leading-none">Tapering Dosage</span>
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className="text-left">
                              <div className="w-full py-2 px-2 sm:px-3">
                                <div className="relative rounded-md flex items-center gap-3">
                                  <div className="relative w-20">
                                    <Input
                                      variant="TRANSPARENT"
                                      type="number"
                                      data-medicine-required-empty={isDoseRequiredAndEmpty ? "true" : undefined}
                                      errorMessages={[
                                        { message: "", type: "required" },
                                        DOSE_DECIMAL_ERROR_MESSAGE,
                                        DOSE_POSITIVE_ERROR_MESSAGE,
                                      ]}
                                      fieldError={
                                        formHook?.formState?.errors
                                          ? formHook?.formState?.errors?.medications
                                            ? formHook?.formState?.errors?.medications?.[index]
                                              ? formHook?.formState?.errors?.medications?.[index]?.dose
                                              : null
                                            : null
                                          : null
                                      }
                                      autoComplete="off"
                                      autoCorrect="off"
                                      autoCapitalize="off"
                                      spellCheck="false"
                                      className="w-full border-[#E2E2E2]"
                                      inputClassName={`ring-1 focus:border-1 focus:ring-1 h-8 ${
                                        isDoseRequiredAndEmpty ? "!ring-red-500 !focus:ring-red-500" : ""
                                      }`}
                                      placeholder={"e.g 10"}
                                      register={formHook.register(`medications.${index}.dose`, {
                                        required: false,
                                        ...DOSE_DECIMAL_VALIDATION,
                                        validate: (value) => {
                                          if (value === null || value === undefined || String(value).trim() === "") {
                                            return true;
                                          }
                                          return Number(value) > 0;
                                        },
                                      })}
                                    />
                                  </div>
                                  <div>
                                    {hasAutoDoseUnit ? (
                                      <div className="min-w-[84px] sm:min-w-[96px] text-xs">
                                        <p className="text-[10px] font-semibold tracking-[0.14em] text-[#9CA3AF] uppercase mb-1">
                                          Auto
                                        </p>
                                        <p className="h-6 px-3 rounded-lg bg-[#F2F2F2] text-[#4B5563] text-xs font-semibold flex items-center">
                                          <span>{formHook.getValues(`medications.${index}.dose_unit`) || selectedSkuForm}</span>
                                        </p>
                                      </div>
                                    ) : (
                                      <div>
                                        <InputWithSuggestionBox
                                          dropdownZIndex={dropdownZIndex}
                                          showAllSuggestionsOnFocus={true}
                                          ref={doseRef}
                                          suggestions={unitItems.map((item) => item.title)}
                                          placeholder="Dose Unit"
                                          formErrors={!!formHook?.formState?.errors?.medications?.[index]?.dose_unit}
                                          defaultInputValue={formHook.getValues(`medications.${index}.dose_unit`)}
                                          onInputChange={(value) => {
                                            formHook.setValue(`medications.${index}.dose_unit`, value);
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="text-left border-none">
                              <div className="py-2 px-2 sm:px-3">
                                <div className="relative rounded-md">
                                  <div className="inset-y-0 right-0 flex">
                                    <div className="flex items-center gap-2">
                                      <div className={`relative ${showXValueInput ? "w-24" : "w-36"}`}>
                                        <InputWithSuggestionBox
                                          dropdownZIndex={dropdownZIndex}
                                          showAllSuggestionsOnFocus={true}
                                          forceSelection={true}
                                          showDropdownIcon={true}
                                          ref={frequencyRef}
                                          inputName={`medications.${index}.frequency`}
                                          mandatoryEmpty={isFrequencyRequiredAndEmpty}
                                          suggestions={frequencySuggestions}
                                          groupedSuggestions={frequencySuggestionGroups}
                                          dropdownTextClassName="text-xs"
                                          placeholder="Frequency"
                                          formErrors={
                                            !!formHook?.formState?.errors?.medications?.[index]?.frequency ||
                                            isFrequencyRequiredAndEmpty
                                          }
                                          defaultInputValue={formHook.getValues(`medications.${index}.frequency`)}
                                          onInputChange={(value) => {
                                            formHook.setValue(`medications.${index}.frequency`, value);
                                            if (!frequencyXValueRequirementMap[value]) {
                                              formHook.setValue(`medications.${index}.x_value`, undefined);
                                            }
                                          }}
                                        // onKeyDown={(e) => handleKeyPress(e, timingRef)}
                                        />
                                      </div>
                                      {showXValueInput && (
                                        <Input
                                          variant="TRANSPARENT"
                                          type="number"
                                          data-medicine-required-empty={isXRequiredAndEmpty ? "true" : undefined}
                                          className="w-14"
                                          inputClassName={`ring-1 focus:border-1 focus:ring-1 h-8 text-center ${
                                            isXRequiredAndEmpty ? "!ring-red-500 !focus:ring-red-500" : ""
                                          }`}
                                          placeholder="X"
                                          fieldError={formHook?.formState?.errors?.medications?.[index]?.x_value}
                                          errorMessages={[{ message: "X value is required.", type: "required" }]}
                                          register={formHook.register(`medications.${index}.x_value`, {
                                            validate: (value) => {
                                              const frequency = formHook.getValues(`medications.${index}.frequency`);
                                              const isRequired = !!frequencyXValueRequirementMap[frequency];
                                              if (!isRequired) return true;
                                              return value && Number(value) > 0 ? true : "X value is required.";
                                            },
                                          })}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="text-left border-none">
                              <div className="py-2 px-2 sm:px-3">
                                <div>
                                  <div className="relative rounded-md">
                                    <div className="inset-y-0 right-0 flex">
                                      <div className="relative">
                                        <InputWithSuggestionBox
                                          dropdownZIndex={dropdownZIndex}
                                          showAllSuggestionsOnFocus={true}
                                          forceSelection={true}
                                          showDropdownIcon={true}
                                          ref={timingRef}
                                          inputName={`medications.${index}.timing`}
                                          mandatoryEmpty={isTimingRequiredAndEmpty}
                                          suggestions={timingSuggestions}
                                          groupedSuggestions={timingSuggestionGroups}
                                          placeholder="Timing"
                                          formErrors={isTimingRequiredAndEmpty}
                                          defaultInputValue={formHook.getValues(`medications.${index}.timing`)}
                                          onInputChange={(value) => {
                                            formHook.setValue(`medications.${index}.timing`, value);
                                          }}
                                        // onKeyDown={(e) => handleKeyPress(e, durationUnitRef)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="text-left">
                              <div className="py-2 px-2 sm:px-3">
                                <div className="">
                                  <div className="relative rounded-md flex gap-3 items-center">
                                    <div className="relative w-24">
                                      <Input
                                        variant="TRANSPARENT"
                                        type="number"
                                        data-medicine-required-empty={isDurationRequiredAndEmpty ? "true" : undefined}
                                        errorMessages={[
                                          { message: "", type: "required" },
                                          DURATION_NUMERIC_ERROR_MESSAGE,
                                          DURATION_NO_DECIMAL_ERROR_MESSAGE,
                                          DURATION_POSITIVE_ERROR_MESSAGE,
                                        ]}
                                        fieldError={
                                          formHook?.formState?.errors
                                            ? formHook?.formState?.errors?.medications
                                              ? formHook?.formState?.errors?.medications?.[index]
                                                ? formHook?.formState?.errors?.medications?.[index]?.duration
                                                : null
                                              : null
                                            : null
                                        }
                                        className="w-full "
                                        inputClassName={`ring-1 focus:border-1 focus:ring-1 h-8 ${
                                          isDurationRequiredAndEmpty ? "!ring-red-500 !focus:ring-red-500" : ""
                                        }`}
                                        placeholder={"e.g 5"}
                                        register={formHook.register(`medications.${index}.duration`, {
                                          required: false,
                                          ...DURATION_NUMBER_FORMAT_VALIDATION,
                                          validate: {
                                            noDecimal: (value) => {
                                              if (value === null || value === undefined || String(value).trim() === "") {
                                                return true;
                                              }
                                              return Number.isInteger(Number(value));
                                            },
                                            positiveValue: (value) => {
                                              if (value === null || value === undefined || String(value).trim() === "") {
                                                return true;
                                              }
                                              return Number(value) > 0;
                                            },
                                          },
                                        })}
                                      />
                                    </div>
                                    <div className="flex items-center h-8">
                                      <span className="text-[#9CA3AF] font-semibold text-xs">days</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="text-left">
                              <div className="w-full py-2 px-2 sm:px-3">
                                <Input
                                  errorMessages={[{ message: "Temperature is required.", type: "required" }]}
                                  fieldError={formHook.formState.errors.medications}
                                  register={formHook.register(`medications.${index}.remark`, {
                                    required: false,
                                  })}
                                  inputClassName="text-xs h-9"
                                  type="text"
                                  placeholder="Remarks"
                                />
                              </div>
                            </td>

                            <td
                              className={`p-2 sm:p-3 text-center ${isSingle
                                ? "rounded-tr-xl rounded-br-xl"
                                : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`.trim()
                                }`}
                            >
                              <button
                                className={`w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px] ${isDeleteDisabled ? "opacity-50 cursor-not-allowed" : ""
                                  }`}
                                disabled={isDeleteDisabled}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isDeleteDisabled) {
                                    return;
                                  }
                                  isChilderRemovefromEMR(index, field);
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="white"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="size-5 text-[#ED0101]"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                  />
                                </svg>
                              </button>
                            </td>
                          </motion.tr>
                          <NestedTaperingDoseV2
                            durations={durations}
                            frequencies={medicineFrequencies || []}
                            routes={routes}
                            timings={medicineTimings || []}
                            units={medicineUnits}
                            mode={"EDIT"}
                            formHook={formHook}
                            nestedIndex={index}
                            isParentSkuFormMandatory={isCurrentSkuFormMandatory}
                            dropdownZIndex={dropdownZIndex}
                          />
                        </>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
          <EMRItemsSuggestionsComponent
            handelCallback={(item, s) => handleAddMedication(item, s)}
            suggestionData={normalizedMedicineSuggestions}
          />
        </div>

        <div className="bg-[#FFFFFF] rounded-xl p-[10px]  mt-2">
          <textarea
            className="w-full   border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
            placeholder="Additional Medication Instructions"
            {...formHook.register("instructions", {
              required: false,
            })}
          ></textarea>
        </div>
      </div>
    </div>
  );
};
export default MedicinesSectionV2;

// medicines_suggestions
